import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
} from "@/components/ui/Chart";
import { useQuery } from "@tanstack/react-query";

import { QUERY_KEY } from "@/constant/queryKey";
import { fetchDashboardData } from "@/services/api-service";
import {
  ADMIN_DASHBOARD_ENDPOINT,
  AdminDashboardPieData,
} from "@/services/core/admin-dashboard-charts.api";
import { useDashboardStore } from "@/store/dashboard-store";
import { useMerchantStore } from "@/store/merchant-store";
import { TrendingUp } from "lucide-react";
import { useMemo } from "react";
import { Cell, Label, Pie, PieChart } from "recharts";
import { PaymentPieChartSkeleton } from "./PaymentPieChartSkeleton";

const pieChartConfig = {
  amount: {
    label: "Transaction Amount",
    color: "hsl(var(--chart-1))",
  },
} satisfies ChartConfig;

const PaymentPieChart = () => {
  const { selectedMerchant } = useMerchantStore();
  const { dateRange } = useDashboardStore();

  const endPoint = `${ADMIN_DASHBOARD_ENDPOINT}/${selectedMerchant?.name}/pie-data`;
  const { data, isLoading } = useQuery({
    queryKey: [
      QUERY_KEY.ADMIN_DASHBOARD_PIE,
      selectedMerchant?.name,
      dateRange,
    ],
    queryFn: () =>
      fetchDashboardData<AdminDashboardPieData>(endPoint, {
        username: selectedMerchant?.name ?? "",
        from: dateRange.from,
        to: dateRange.to,
      }),
  });

  const channelStats = useMemo(() => {
    if (!data?.payload) return [];

    const blueShades = ["#2563EB", "#3B82F6", "#60A5FA", "#93C5FD"];

    return data.payload.map((el, idx) => ({
      ...el,
      hex: blueShades[idx],
    }));
  }, [data]);

  const totalAmount =
    channelStats?.reduce((acc, curr) => acc + curr.amount, 0) ?? 0;

  if (isLoading) {
    return <PaymentPieChartSkeleton />;
  }

  if (!data?.payload?.length) {
    return (
      <Card className="flex flex-col">
        <CardHeader className="items-center pb-0">
          <CardTitle>Payment Methods Distribution</CardTitle>
          <CardDescription>Total Transaction Amount</CardDescription>
        </CardHeader>
        <CardContent className="flex h-[400px] items-center justify-center px-20">
          <div className="text-center">
            <p className="text-muted-foreground text-sm">
              No payment distribution data available for the selected date range
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="flex flex-col">
      <CardHeader className="items-center pb-0">
        <CardTitle>Payment Methods Distribution</CardTitle>
        <CardDescription>Total Transaction Amount</CardDescription>
      </CardHeader>
      <CardContent className="flex-1 pb-0">
        <ChartContainer
          config={pieChartConfig}
          className="mx-auto aspect-square max-h-[350px]"
        >
          <PieChart>
            <ChartTooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div className="bg-background rounded-lg border p-2 shadow-sm">
                      <div className="grid gap-2">
                        <div className="flex flex-col">
                          <span className="text-muted-foreground text-[0.70rem] uppercase">
                            {data.name}
                          </span>
                          <span className="font-bold">
                            ${data.amount.toLocaleString()}
                          </span>
                          <span className="text-muted-foreground text-xs">
                            {((data.amount / totalAmount) * 100).toFixed(1)}% of
                            total
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Pie
              data={channelStats}
              dataKey="amount"
              nameKey="name"
              innerRadius={95}
              strokeWidth={5}
            >
              {channelStats?.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.hex} />
              ))}
              <Label
                content={({ viewBox }) => {
                  if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                    return (
                      <text
                        x={viewBox.cx}
                        y={viewBox.cy}
                        textAnchor="middle"
                        dominantBaseline="middle"
                      >
                        <tspan
                          x={viewBox.cx}
                          y={viewBox.cy}
                          className="fill-foreground text-3xl font-bold"
                        >
                          ${totalAmount.toLocaleString()}
                        </tspan>
                        <tspan
                          x={viewBox.cx}
                          y={(viewBox.cy || 0) + 24}
                          className="fill-muted-foreground"
                        >
                          Total Amount
                        </tspan>
                      </text>
                    );
                  }
                }}
              />
            </Pie>
          </PieChart>
        </ChartContainer>
      </CardContent>
      <CardFooter className="flex-col gap-2 text-sm">
        <div className="mb-2 flex flex-wrap items-center gap-3">
          {channelStats?.map((channel) => (
            <div key={channel.name} className="flex items-center gap-1.5">
              <div
                className="h-3 w-3 rounded-full"
                style={{ backgroundColor: channel.hex }}
              />
              <span className="text-muted-foreground text-xs">
                {channel.name}
              </span>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 leading-none font-medium">
          Credit Card leads by{" "}
          {(((channelStats?.[0]?.amount ?? 0) / totalAmount) * 100).toFixed(1)}
          %
          <TrendingUp className="h-4 w-4" />
        </div>
        <div className="text-muted-foreground leading-none">
          Based on transaction amount distribution
        </div>
      </CardFooter>
    </Card>
  );
};

export default PaymentPieChart;
